import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography, Alert, Skeleton, Button } from "@mui/material";
import { MdOpenInNew, MdImageNotSupported } from "react-icons/md";

import { Surface, SectionTitle } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

/**
 * The partner's KYC images.
 *
 * Read-only on purpose: these are uploaded by the partner from their own app,
 * and an admin-side upload would put a document on someone's identity record
 * that they never provided. What ops needs here is to *see* what was uploaded
 * so they can decide whether to approve — which is the tab next door.
 *
 * Every slot is listed, uploaded or not, because the useful question is
 * "what is missing", and a grid of only the present ones cannot answer it.
 */
function Slot({ doc }) {
  const [broken, setBroken] = useState(false);

  return (
    <Box
      sx={{
        border: `1px solid ${doc.uploaded ? T.border : tint(T.error, 0.3)}`,
        borderRadius: 2.5,
        overflow: "hidden",
        bgcolor: doc.uploaded ? T.surface : tint(T.error, 0.03),
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          height: 150,
          display: "grid",
          placeItems: "center",
          bgcolor: T.borderSoft,
          position: "relative",
        }}
      >
        {doc.uploaded && !broken ? (
          <Box
            component="img"
            src={doc.url}
            alt={doc.label}
            onError={() => setBroken(true)}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <Stack alignItems="center" spacing={0.6} sx={{ color: T.subtle }}>
            <MdImageNotSupported size={26} />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
              {/* A stored URL that will not load is a different problem from
                  a document that was never uploaded, and ops has to be able
                  to tell them apart. */}
              {doc.uploaded ? "Stored, but will not load" : "Not uploaded"}
            </Typography>
          </Stack>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.4, py: 1, gap: 1 }}
      >
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: T.ink }}>
          {doc.label}
        </Typography>
        {doc.uploaded && (
          <Button
            size="small"
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 0, p: 0.4, color: T.secondary }}
          >
            <MdOpenInNew size={16} />
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default function DocumentsTab({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .getDocuments(partnerId)
      .then((res) => alive && setData(res))
      .catch(
        (err) =>
          alive &&
          setError(err?.response?.data?.message || "Could not load documents.")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [partnerId]);

  if (loading) {
    return (
      <Grid container spacing={2}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: 2.5 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const missing = (data?.total || 0) - (data?.uploaded || 0);

  return (
    <Stack spacing={2}>
      <Surface sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <SectionTitle sx={{ mb: 0 }}>KYC documents</SectionTitle>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: missing ? T.warning : T.success,
            }}
          >
            {data?.uploaded ?? 0} of {data?.total ?? 0} uploaded
            {missing ? ` · ${missing} missing` : ""}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: "0.8rem", color: T.subtle, mt: 0.6 }}>
          Partners upload these from their own app. They cannot be added here —
          only reviewed.
        </Typography>
      </Surface>

      <Grid container spacing={2}>
        {(data?.documents || []).map((doc) => (
          <Grid item xs={12} sm={6} md={3} key={doc.key}>
            <Slot doc={doc} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}
